import type { AiAgentId } from '@fruitful/llm-service-shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToastController } from 'smoothie';

import { MutationErrorToastOptions } from '../../common/components/Toasts';
import { llmServiceFetcher } from '../LlmServiceFetcher';

export type QASuiteStatus = 'pending' | 'running' | 'completed' | 'incomplete';

export interface QASuiteSummary {
    id: string;
    agentId: AiAgentId;
    promptName?: string;
    status: QASuiteStatus;
    total: number;
    pending: number;
    running: number;
    completed: number;
    error: number;
    createdAt: string;
    updatedAt: string;
    cases: Array<{
        caseId: string;
        status: 'pending' | 'running' | 'completed' | 'error';
        attemptCount: number;
        threadId?: string;
        error?: string;
        updatedAt: string;
    }>;
}

interface CreateQASuiteInput {
    agentId: AiAgentId;
    promptName?: string;
    promptInstructions?: string;
    caseIds?: string[];
}

interface CreateQASuiteResponse {
    suiteId: string;
    accepted: number;
}

const suitePath = (agentId: AiAgentId, suiteId?: string): string =>
    `/agents/${encodeURIComponent(agentId)}/qa/suites${
        suiteId == null ? '' : `/${encodeURIComponent(suiteId)}`
    }`;

export const useCreateQASuite = () => {
    const toast = useToastController();

    return useMutation({
        mutationFn: async ({
            agentId,
            ...body
        }: CreateQASuiteInput): Promise<CreateQASuiteResponse> =>
            await llmServiceFetcher<CreateQASuiteResponse>({
                endpoint: suitePath(agentId),
                method: 'POST',
                body: JSON.stringify(body),
            }),
        onError: (error) => {
            toast.show(
                'Failed to start QA suite.',
                MutationErrorToastOptions(error.message),
            );
        },
    });
};

export const useQASuite = (agentId: AiAgentId, suiteId: string) =>
    useQuery({
        queryKey: ['qaSuite', agentId, suiteId],
        queryFn: async (): Promise<QASuiteSummary | null> =>
            await llmServiceFetcher<QASuiteSummary | null>({
                endpoint: suitePath(agentId, suiteId),
            }),
        refetchInterval: (query) => {
            const status = query.state.data?.status;
            return status === 'pending' || status === 'running' ? 5_000 : false;
        },
        refetchOnWindowFocus: true,
    });

export const useQASuites = (agentId: AiAgentId) =>
    useQuery({
        queryKey: ['qaSuites', agentId],
        queryFn: async (): Promise<QASuiteSummary[]> =>
            await llmServiceFetcher<QASuiteSummary[]>({
                endpoint: suitePath(agentId),
            }),
        refetchInterval: 5_000,
        refetchOnWindowFocus: true,
    });

export const useResumeQASuite = (agentId: AiAgentId, suiteId: string) => {
    const queryClient = useQueryClient();
    const toast = useToastController();

    return useMutation({
        mutationFn: async (): Promise<CreateQASuiteResponse> =>
            await llmServiceFetcher<CreateQASuiteResponse>({
                endpoint: `${suitePath(agentId, suiteId)}/resume`,
                method: 'POST',
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['qaSuite', agentId, suiteId],
            });
        },
        onError: (error) => {
            toast.show(
                'Failed to resume QA suite.',
                MutationErrorToastOptions(error.message),
            );
        },
    });
};
